import THREE from "three";
import { getGlobal } from "../../compat";
import { cubicBezier } from "../../wgs/render/RenderContext";
import { FacetSpaces, FacetTypes, UNASSIGNED } from "./Facets";
import { ElementFlags } from "../schema/dt-schema";
import { AGGREGATE_SELECTION_CHANGED_EVENT } from "../../application/EventTypes";

import { createFloorFootprint, getFloorBoxFuzzy } from "./floor-footprint";
import { SketchToolsControls } from "../sketchtools/SketchToolsControls";
import { VertexBufferBuilder } from "../../wgs/scene/VertexBufferBuilder";
import { VertexEnumerator } from "../../wgs/scene/VertexEnumerator";
import { BufferGeometryUtils } from "../../wgs/scene/BufferGeometry";
import { LmvMatrix4 } from "../../wgs/scene/LmvMatrix4";
import { cutMesh } from "./cut-mesh";
import { DtConstants } from "../schema/DtConstants";
import { LmvBox3 } from "../../wgs/scene/LmvBox3";
import { getBoxMesh } from "./box-mesh";
import { TandemCategories } from "../schema/TandemCategories";

export const HOVER_ROOMS_OVERLAY = "hover_rooms";
export const HOVER_FLOORS_OVERLAY = "hover_floors";
export const ELEMENT_CLONE_OVERLAY = "streams";
export const BACK_ELEMENT_CLONE_OVERLAY = "background_elements";
const SELECT_FLOORS_OVERLAY = "select_floors";
const NAME_PREFIX_LEVEL = "level-filter:";
const NAME_PREFIX_SPACE = "space-filter:";
const NAME_PREFIX_FLOORPLAN = "floorplan:";
export const NAME_PREFIX_TRACE = "system-trace:";

const COLOR_FILTER_HIGHLIGHT_ABGR = 0xffd79606;
export const COLOR_TRACE_HIGHLIGHT_ABGR = 0xff60c000;
const COLOR_TRACE_HIGHLIGHT_RGB = 0x00c060;

const _window = getGlobal();

function getOverlayMaterial(color, opacity, packedNormals) {
  const material = new THREE.MeshPhongMaterial({
    color,
    specular: 0x080808,
    ambient: 0,
    opacity,
    reflectivity: 0,
    transparent: true,
    alphaTest: 0.0
  });
  material.packedNormals = packedNormals;
  material.depthWrite = false;
  material.depthTest = true;
  material.opacityTarget = opacity; //custom prop to change opacity on the fly
  return material;
}

export function createOverlay(viewer, name, color, opacityTop, opacityBehind, packedNormals) {
  const materialTop = opacityTop ? getOverlayMaterial(color, opacityTop, packedNormals) : null;
  const materialBehind = opacityBehind ? getOverlayMaterial(color, opacityBehind, packedNormals) : null;

  const overlay = viewer.impl.createOverlayScene(name, materialTop, materialBehind);

  let c = new THREE.Color(color);

  viewer.impl.overlayScenes[name].edgeColor = new THREE.Vector4(c.r, c.g, c.b, 1);

  return overlay;
}

function vbbToMesh(vbb, material, modelId) {
  let mdata = { mesh: vbb.toMesh() };

  BufferGeometryUtils.meshToGeometry(mdata);

  let bg2d = mdata.geometry;

  let mesh2d = new THREE.Mesh(bg2d, material);

  //mesh2d.matrix.copy(this.planeToWorld);
  mesh2d.matrixAutoUpdate = false;
  mesh2d.frustumCulled = false;

  mesh2d.modelId = modelId || 0; // So we can look it up later

  return mesh2d;
}

function create2dMaterial(viewer, name, hatchStyle, hidden) {

  //Used to disambiguate -- otherwise create2DMaterial returns the same instance
  //based on its own input parameters only. But we also modify the returned material later on
  //in this function, which menas we cannot reuse a common instance
  const namePrefix = { id: name };

  let mat2dname = viewer.impl.matman().create2DMaterial(
    namePrefix, {
      isScreenSpace: true,
      hasLineStyles: true,
      skipLine3D: false,
      hatchPattern: !!hatchStyle
    },
    false, false
  );

  let mat2d = viewer.impl.matman().findMaterial(namePrefix, mat2dname);

  mat2d.transparent = true;
  mat2d.depthTest = true;
  mat2d.polygonOffset = true;
  //A quite large offset is needed when we want to show 2d edges drawn using the line shader (so, screen aligned polyfons)
  //that are also several pixel wide. Imagine a case where this kind of edge is at a 90 degree wall corner and is 6 pixels wide
  //-- a 3 pixel distance from the centerline could be many depth units behind the wall corner. So this is not perfect,
  //in some cases one needs 1000 units to make things show on top.
  mat2d.polygonOffsetFactor = hidden ? 10 : -10;
  mat2d.polygonOffsetUnits = hidden ? 10 : -10;
  //mat2d.cutplanes = _otherCutPlanes; // make sure that cap meshes respect cutplanes from other tools
  mat2d.supportsMrtNormals = true;

  if (hatchStyle) {
    mat2d.hatchParams = new THREE.Vector2(hatchStyle.slope, hatchStyle.period);
    mat2d.hatchTintColor = new THREE.Color(hatchStyle.tintColor);
    mat2d.hatchTintIntensity = hatchStyle.intensity;
    mat2d.hatchPattern = true;
  }

  return mat2d;
}

function createOverlay2d(viewer, name, wantTop, wantBehind) {

  const material = create2dMaterial(viewer, name, null, wantBehind);

  const overlay = viewer.impl.createOverlayScene(name, wantTop ? material : null, wantBehind ? material : null);

  return overlay;
}

function getContrastColor(viewer, c) {
  if (viewer.impl.isDarkBackground()) {
    let b = Math.sqrt((c >> 16 & 0xff) / 255.0) * 255.0;
    let g = Math.sqrt((c >> 8 & 0xff) / 255.0) * 255.0;
    let r = Math.sqrt((c & 0xff) / 255.0) * 255.0;
    return c & 0xff000000 | b << 16 | g << 8 | r;
  } else {
    return c;
  }
}

function getContrastColorV4(viewer, v) {
  if (viewer.impl.isDarkBackground()) {
    v.x = Math.sqrt(v.x);
    v.y = Math.sqrt(v.y);
    v.z = Math.sqrt(v.z);
  } else {

  }

  return v;
}


function createBackfaceMaterial(viewer) {

  let hiMat = getOverlayMaterial(getContrastColor(viewer, 0x7f7f7f), 0.75, true);

  //A material that renders back faces only, and is configured for HDR rendering (so not in overlay, but in the main color pass)
  viewer.impl.matman().addHDRMaterial("room-highlight", hiMat);
  hiMat.side = THREE.BackSide;
  hiMat.sideLocked = true;
  hiMat.depthWrite = true;

  hiMat.polygonOffset = true;
  hiMat.polygonOffsetFactor = -1;
  hiMat.polygonOffsetUnits = -1;
  //hiMat.isRoomMaterial = true;

  if (true) {
    //Negative value for hatch period indicates we want a cross-hatch pattern (specifically for room geometry highlights)
    hiMat.hatchParams = new THREE.Vector2(1.0, -1);
    hiMat.hatchTintColor = new THREE.Color(0x007fff);
    hiMat.hatchTintIntensity = 0.5;
    hiMat.hatchPattern = true;
  }


  return hiMat;
}

//Used as a poor man's depth material -- it writes to the depth buffer and also has an outline, but the
//triangles themselves are nearly invisible. Ideally we'd want them fully invisible, but that would
//require a dedicated material or some extra hacks in the Uber shader that seemed too daunting. So
//we use minimal opacity that will pass the alpha test instead.
function createDepthWriteMaterial(viewer) {

  let hiMat = getOverlayMaterial(getContrastColor(viewer, 0x7f7f7f), 1.0 / 255.0, false);

  viewer.impl.matman().addHDRMaterial("floor-highlight", hiMat);
  hiMat.side = THREE.FrontSide;
  hiMat.depthWrite = true;

  return hiMat;
}


const GLOW_COLOR = new THREE.Color( /*"#6ac0e7"*/"#eecc22");

let _hoverFadeId, _hoverStartTimer;

export class FacetVizEffects {
  constructor(facility, options) {
    this.facility = facility;

    this.options = {
      easeSpeed: 0.004,
      easeCurve: [1, 0, 0.75, 1],
      ...options
    };

    this._floorHighlightCache = {};
    this._floorMeshQueue = new Map();
    this._edgeMeshCache = new Map();
    this._roomBoundaryCache = new Map();

    this.glowHighlight = "";

    this.boundSelectionChange = this.onSelectionChanged.bind(this);

    this.highlightingEnabled = true;

    this.sketchToolsControls = new SketchToolsControls(facility);

    this.floorplanEnabled = true;
    this.floorBoundaryVizEnabled = true;
  }

  setViewer(viewer) {
    this.viewer = viewer;

    this.roomBackfaceMaterial = createBackfaceMaterial(this.viewer);
    this.room2dMaterial = create2dMaterial(this.viewer, "room2d", null, false);

    this.roomSceneBackface = new THREE.Scene();
    this.roomSceneBackface.matrixAutoUpdate = false;
    this.roomSceneWireframe = new THREE.Scene();
    this.roomSceneWireframe.matrixAutoUpdate = false;

    this.hoverRoomsOverlay = createOverlay(viewer, HOVER_ROOMS_OVERLAY, 0x6ac0e7, 0.5, 0.25, true);
    this.hoverFloorsOverlay = createOverlay(viewer, HOVER_FLOORS_OVERLAY, 0x6ac0e7, 0.5, 0.25, false);
    this.elementsOverlay = createOverlay(viewer, ELEMENT_CLONE_OVERLAY, COLOR_TRACE_HIGHLIGHT_RGB, 1.0, 0.15, true);
    //Sort order needed so that these draw under the selection highlight.
    this.elementsOverlay.sortOrder = -1;
    this.elementsOverlayBack = createOverlay(viewer, BACK_ELEMENT_CLONE_OVERLAY, 0xff8000, 1.0, 0.15, true);
    this.elementsOverlayBack.sortOrder = -2;

    this.floorsOverlay = createOverlay(viewer, SELECT_FLOORS_OVERLAY, 0x7cb0fb, 0.5, 0.25, true);
    if (!this.viewer.hasEventListener(AGGREGATE_SELECTION_CHANGED_EVENT, this.boundSelectionChange)) {
      this.viewer.addEventListener(AGGREGATE_SELECTION_CHANGED_EVENT, this.boundSelectionChange);
    }

    this.levelSceneWireframe = createOverlay2d(this.viewer, "level-outline-edge", true, false);

    this.depthWriteMaterial = createDepthWriteMaterial(this.viewer);
    this.levelSceneFace = new THREE.Scene();
    this.levelSceneFace.sortOrder = -1;
    this.levelSceneFace.clearDepthBuffer = true;
    this.levelSceneFace.edgeColor = getContrastColorV4(viewer, new THREE.Vector4(0.25, 0.25, 0.25, 1));
    this.viewer.impl.addMainPassOverlay(this.levelSceneFace, true);

    if (this.options.enableSketchTools) {
      if (this.sketchToolsControls.viewer !== viewer) {
        this.sketchToolsControls.init(viewer);
      }
    }

    this.sectionedElements = [];
    this.clonedMaterials = {};

    //this._dbg2d();
  }

  enableHighlighting(enable) {
    this.highlightingEnabled = enable;
  }

  /**
   * @private
   */
  addFloorMeshToOverlay(levelFacets, floorName) {

    //For combined levels, multiple selected level elements will map to
    //the same floor facet, and we have to avoid showing its geometry multiple times
    if (this.floorsOverlay.scene.getObjectByName(floorName)) {
      return;
    }

    const floorFacet = levelFacets.find((l) => l.id === floorName);
    const cachedFloorMesh = this.getFloorMesh(floorFacet);
    if (cachedFloorMesh) {
      // clone cached floor mesh since it is also used by the mouse hover.
      const cloneMesh = new THREE.Mesh(cachedFloorMesh.geometry, cachedFloorMesh.material);
      cloneMesh.matrix.copy(cachedFloorMesh.matrix);
      cloneMesh.matrixWorld.copy(cachedFloorMesh.matrixWorld);
      cloneMesh.matrixAutoUpdate = false;
      cloneMesh.matrixWorldNeedsUpdate = false;
      cloneMesh.name = "overlay " + cachedFloorMesh.name;

      this.viewer.impl.addOverlay(SELECT_FLOORS_OVERLAY, cloneMesh);
    }
  }

  /**
   * @private
   */
  onSelectionChanged(event) {
    const selection = event.selections;

    this.viewer.impl.clearOverlay(SELECT_FLOORS_OVERLAY);

    const facetIdx = this.facility.facetsManager.getFacetDefs().findIndex((f) => f.id === FacetTypes.levels);
    if (facetIdx === -1) {
      return;
    }

    const levelFacets = this.facility.facetsManager.getFacets()[facetIdx];

    // add the floors geometries to the floor selection overlay
    for (let s of selection) {
      let dbId2flags = s.model.getData().dbId2flags;
      if (!dbId2flags) continue;

      for (let dbId of s.dbIdArray) {

        if (dbId2flags[dbId] !== ElementFlags.Level) {
          continue;
        }

        const it = s.model.getInstanceTree();
        const floorName = it.getNodeName(dbId, false);
        this.addFloorMeshToOverlay(levelFacets, floorName);
      }
    }
  }

  /**
   * @private
   */
  startHoverOverlayFade(overlayName, mesh) {

    const viewerImpl = this.viewer.impl;

    _hoverStartTimer = setTimeout(() => {
      let { easeCurve, easeSpeed } = this.options;
      let lastObjTime;
      let finalChange = false;

      // Add mesh to overlayScene but wait for invalidation during fadeloop
      let overlayScene = viewerImpl.overlayScenes[overlayName];

      if (Array.isArray(mesh)) {
        for (let m of mesh) {
          overlayScene.scene.add(m);
        }
      } else {
        overlayScene.scene.add(mesh);
      }

      (function fadeLoop(highResTimeStamp) {

        if (lastObjTime === undefined) {
          lastObjTime = highResTimeStamp;
        }

        // Queue next overlay update if final state isn't reached
        if (!finalChange) {
          _hoverFadeId = _window.requestAnimationFrame(fadeLoop);
        }

        let t = (highResTimeStamp - lastObjTime) * easeSpeed;
        t = Math.min(t, 1.0);

        const opacityModifier = cubicBezier(easeCurve, t);
        overlayScene.materialPre.opacity = overlayScene.materialPre.opacityTarget * opacityModifier;
        overlayScene.materialPost.opacity = overlayScene.materialPost.opacityTarget * opacityModifier;
        overlayScene.edgeColor.setW(opacityModifier); // assuming final edge opacity is always 1

        overlayScene.materialPre.uniformsNeedUpdate = true;
        overlayScene.materialPost.uniformsNeedUpdate = true;

        finalChange = opacityModifier === 1;

        viewerImpl.invalidate(false, false, true);
      })(performance.now());
    }, 20);
  }

  /**
   * Calculate a convex hull mesh from all the elements with wall/floor category
   * @param {FacetNode} item Map of id sets by urn
   * @param {boolean} [useCacheOnly] Cached mesh if present. Will not try to create one.
   */
  getFloorMesh(item, useCacheOnly) {
    let cached = this._floorHighlightCache[item.id];
    const props = { elev: item.elev, count: item.count, order: item.order, isComplete: true };

    if (cached?.mesh || useCacheOnly) {
      // If there's a cached mesh, show it
      // It will be replaced on the fly if there are changes
      return cached?.mesh;
    }

    if (cached?.isComplete && cached.elev === props.elev && cached.count === props.count && cached.order === props.order) {
      return null;
    } else {
      // Update cached props for the current pass
      cached = this._floorHighlightCache[item.id] = { ...cached, ...props };
    }

    //let t0 = performance.now();
    createFloorFootprint(this.facility, item, cached);
    //let t1 = performance.now();
    //console.log(item.id, t1 - t0);

    return cached?.mesh;
  }

  isFloorHighlightComplete(item) {
    return this._floorHighlightCache[item.id]?.isComplete;
  }

  /** Used to invalidate the cached floor footprints used by level highlights. */
  invalidateFloorHighlights() {
    this._floorHighlightCache = {};
  }

  /**
   * Highlight a floor
   * Calculate and show a convex hull from all the elements with wall/floor category
   * @param {FacetNode} item Map of id sets by urn
   */
  addFloorHighlight(item) {
    const mesh = this.getFloorMesh(item);
    if (mesh) {
      this.startHoverOverlayFade(HOVER_FLOORS_OVERLAY, mesh);
    }
  }

  /**
   * Highlight a space `node` by adding to the "hovering" overlay scene
   * Add a facetVizEffects for each fragId that has getVizmesh mesh geometry
   * @param {FacetNode} node - One of the Facet items
   */
  addSpaceHighlight(node) {

    const model = node.roomSrcModel ?? node.model;
    let meshes = model.getProxyMeshes(node.dbId);

    if (!meshes.length) {
      let box = new THREE.Box3();
      model.getElementBounds(node.dbId, box);

      if (box.isEmpty()) {
        return;
      }

      const mesh = getBoxMesh(box, null, true);
      mesh.name = "space box" + node.name;

      meshes = [mesh];
    }

    this.startHoverOverlayFade(HOVER_ROOMS_OVERLAY, meshes);

    //this.addElementHighlight(node.roomSrcModel ?? node.model, node.dbId, HOVER_ROOMS_OVERLAY);
  }

  /**
   * @param {FacetDef} facetDef
   * @param {FacetNode} node
   */
  highlightFacet(facetDef, node) {
    if (!this.highlightingEnabled) {
      return;
    }

    if (node.id === UNASSIGNED) {
      return;
    }

    if (facetDef.id === FacetTypes.spaces) {
      this.addSpaceHighlight(node);
    } else if (facetDef.id === FacetTypes.levels) {
      this.addFloorHighlight(node);
    } else if (facetDef.id === FacetTypes.categories && this.glowHighlight === facetDef.id) {
      this.highlightCategory(node.id);
    } else if (facetDef.id === FacetTypes.tandemCategories && this.glowHighlight === facetDef.id) {
      this.highlightTandemCategory(node.id);
    } else if (facetDef.id === FacetTypes.systemClasses && this.glowHighlight === facetDef.id) {
      this.highlightSystemClass(node.id);
    } else if (facetDef.id === FacetTypes.mepSystems && this.glowHighlight === facetDef.id) {
      this.highlightSystem(node.glowEffectId);
    }
  }

  clearFacetHighlight() {
    this.clearHoveringOverlay();
    this.highlightByObjectFlag(0, GLOW_COLOR, 0);
  }

  /**
   * Changes all room materials to opaque, for better space visualization during color-by room
   * @param {Boolean} opaque -- opaque rendering on or off
   */
  makeRoomsOpaque(opaque) {
    this.viewer.impl.matman().forEach((mat) => {
      if (mat.isRoomMaterial) {
        mat.depthWrite = opaque;
        mat.opacity = opaque ? 1 : 0.001;
        mat.needsUpdate = mat.uniformsNeedUpdate = true;
      }
    });
  }

  makeRoomsVisible(visible) {
    this.viewer.impl.matman().forEach((mat) => {
      if (mat.isRoomMaterial && !mat.depthWrite) {
        // bump opacity to above alpha cut off unless opaque already
        mat.opacity = visible ? 0.012 : 0.001;
        mat.uniformsNeedUpdate = true;
      }
    });
  }

  /**
   * @param {FacetLevels} facetDef
   * @param {FacetNode} mergedFacet
   */
  sectionVerticalElements(facetDef, mergedFacet) {

    let matman = this.facility.viewer.impl.matman();

    //Clear any existing material overrides
    for (let matId in this.clonedMaterials) {
      let clonedMat = this.clonedMaterials[matId];

      for (let i = 0; i < clonedMat.usedBy.length; i++) {
        let [model, fragId] = clonedMat.usedBy[i];

        // The model may have been unloaded at this point already.
        model.getFragmentList()?.setMaterial(fragId, clonedMat.originalMat);
      }

      matman.removeMaterial(clonedMat.name);
    }

    this.clonedMaterials = {};

    if (!mergedFacet) {
      return;
    }

    let count = 0;
    let count2 = 0;

    let fbox = getFloorBoxFuzzy(this.facility, mergedFacet, 0.9, false);

    if (!fbox) {
      return;
    }

    let cutplanes = [
    new THREE.Vector4(0, 0, -1, fbox.min.z),
    new THREE.Vector4(0, 0, 1, -fbox.max.z)];


    const renderer = this.facility.viewer.impl.renderer();
    if (renderer.useWebGPU) {
      renderer.setLevelCutPlanes(cutplanes);
    }

    for (let [model, ids] of mergedFacet.ids) {

      let fl = model.getFragmentList();
      let it = model.getInstanceTree();
      let dbId2levelId = model.getData().dbId2levelId;
      let dbId2topLevel = model.getData().dbId2topLevel;

      for (let dbId of ids) {
        let baseLevel = dbId2levelId[dbId];
        let topLevel = dbId2topLevel[dbId];
        if (topLevel) {
          it.enumNodeFragments(dbId, (fragId) => {
            let mat = fl.getMaterial(fragId);
            let matId = fl.getMaterialId(fragId);

            if (!mat) {
              //It's possible some elements are not yet loaded, if we started with a saved view
              return;
            }

            let cloned = this.clonedMaterials[matId];
            if (!cloned) {
              cloned = matman.cloneMaterial(mat, model);
              cloned.localCutplanes = cutplanes;
              cloned.needsUpdate = true;
              cloned.originalMatId = matId;
              cloned.originalMat = mat;

              this.clonedMaterials[matId] = cloned;
              cloned.usedBy = [];
              cloned.name = matman._getMaterialHash(model, mat.hash) + ":level-section";

              //The material needs to be added to the material manager so that
              //visualization settings changes are taken into account.
              matman.addMaterial(cloned.name, cloned, true);

              //Done after addMaterial so it doesn't cause turning the setting on globally
              cloned.side = THREE.DoubleSide;
            }

            fl.setMaterial(fragId, cloned);
            cloned.usedBy.push([model, fragId]);

          }, false);
          count++;
        } else if (topLevel === baseLevel) {
          count2++;
        }
      }
    }

  }

  #addEdgeMeshToVbb(geom, matrixWorld, dbId, vbbFront, color) {

    if (!geom.iblines) {
      return;
    }

    const dpr = this.viewer.impl.glrenderer().getPixelRatio();
    const LineWidthPx = 2;

    const vcount = VertexEnumerator.getVertexCount(geom);
    const worldPts = new Float32Array(vcount * 3);

    VertexEnumerator.enumMeshVertices(geom, (p, n, uv, i) => {
      worldPts[3 * i] = p.x;
      worldPts[3 * i + 1] = p.y;
      worldPts[3 * i + 2] = p.z;
    }, matrixWorld);

    //Add thick contrast line first
    for (let i = 0; i < geom.iblines.length; i += 2) {

      const i1 = geom.iblines[i];
      const i2 = geom.iblines[i + 1];
      const p1x = worldPts[3 * i1];
      const p1y = worldPts[3 * i1 + 1];
      const p1z = worldPts[3 * i1 + 2];
      const p2x = worldPts[3 * i2];
      const p2y = worldPts[3 * i2 + 1];
      const p2z = worldPts[3 * i2 + 2];

      vbbFront.addSegment3D(p1x, p1y, p1z, p2x, p2y, p2z, 0, -(LineWidthPx + 2) * dpr, 0xffffffff, dbId, 0);
    }

    const c = color || 0xff000000;

    //Add thin highlight line on top
    for (let i = 0; i < geom.iblines.length; i += 2) {

      const i1 = geom.iblines[i];
      const i2 = geom.iblines[i + 1];
      const p1x = worldPts[3 * i1];
      const p1y = worldPts[3 * i1 + 1];
      const p1z = worldPts[3 * i1 + 2];
      const p2x = worldPts[3 * i2];
      const p2y = worldPts[3 * i2 + 1];
      const p2z = worldPts[3 * i2 + 2];

      vbbFront.addSegment3D(p1x, p1y, p1z, p2x, p2y, p2z, 0, -LineWidthPx * dpr, c, dbId, 0);
    }

  }

  addSpaceEdgeHighlight(facetId, model, dbId, color, forceUpdate) {

    let edgeMesh = this._edgeMeshCache.get(facetId);
    if (edgeMesh) {
      if (forceUpdate) {
        this._edgeMeshCache.delete(facetId);
        this.roomSceneWireframe.remove(edgeMesh.front);
      } else {
        this.roomSceneWireframe.add(edgeMesh.front);
        return;
      }
    }

    const it = model.getInstanceTree();
    const fl = model.getFragmentList();

    const vbbFront = new VertexBufferBuilder();
    //const vbbHidden = new VertexBufferBuilder();

    let isEmpty = true;

    it.enumNodeFragments(dbId, (fragId) => {

      const geom = fl.getGeometry(fragId);

      if (!geom) return;

      isEmpty = false;

      const matrixWorld = new LmvMatrix4();
      fl.getWorldMatrix(fragId, matrixWorld);

      this.#addEdgeMeshToVbb(geom, matrixWorld, dbId, vbbFront, color);

    });

    if (isEmpty) {
      return;
    }

    let frontMesh = vbbToMesh(vbbFront, this.room2dMaterial, model.id);
    frontMesh.name = facetId;

    this._edgeMeshCache.set(facetId, { front: frontMesh });
    this.roomSceneWireframe.add(frontMesh);
  }

  addSpaceFilterViz(node, forceUpdate) {
    const model = node.roomSrcModel ?? node.model;
    const dbId = node.dbId;
    const id = NAME_PREFIX_SPACE + node.id;
    this.addSpaceBoundaryViz(id, model, dbId, COLOR_FILTER_HIGHLIGHT_ABGR, forceUpdate);
  }

  addSpaceBoundaryViz(id, model, dbId, color, forceUpdate) {

    this.addSpaceEdgeHighlight(id, model, dbId, color, forceUpdate);

    const meshes = model.getProxyMeshes(dbId, this.roomBackfaceMaterial);
    if (!meshes.length || !meshes[0]) {
      return;
    }

    //this.viewer.impl.addMultipleOverlays(overlayId || ELEMENT_CLONE_OVERLAY, [meshes[0]]);
    meshes[0].name = id;
    this.roomSceneBackface.add(meshes[0]);

    this.viewer.impl.addMainPassOverlay(this.roomSceneBackface);
    this.viewer.impl.addMainPassOverlay(this.roomSceneWireframe);
  }

  #queueFloorMeshComputation(item) {
    this._floorMeshQueue.set(item.node.id, item);
  }

  addFloorEdgeHighlight(node, forceCompute) {

    //because this triggers full repaint (edge mesh is draw in main pass), we prefer to do it immediately
    //in order to avoid a second model repaint, so we avoid using the queue.
    //In case things change and we need multiple floor footprints highlighted using this code,
    //we need to reconsider.
    let protoMesh = this.getFloorMesh(node, true /*!forceCompute*/);

    if (!protoMesh) {
      this.#queueFloorMeshComputation({ node, whichEffect: 1 });
      return;
    }

    if (!protoMesh?.geometry) {
      return;
    }

    //Use correct Level ID or choose one if there are multiple combined levels in the facet
    const models = this.facility.facetsManager.models;
    let dbId = 0;
    let modelId = 0;
    for (const model of models) {
      const levels = model.getLevels();
      for (const lId in levels) {
        const level = levels[lId];
        if (level.name === node.id) {
          dbId = parseInt(lId);
          modelId = model.id;
          break;
        }
      }
      if (dbId && model.isPrimaryModel()) {
        break;
      }
    }

    const cacheId = NAME_PREFIX_LEVEL + node.id;
    const cacheEntry = this._edgeMeshCache.get(cacheId);
    if (cacheEntry) {
      //this.levelSceneWireframe.scene.add(cacheEntry.front);
      this.roomSceneWireframe.add(cacheEntry.front);
    } else {
      let vbb = new VertexBufferBuilder();

      this.#addEdgeMeshToVbb(protoMesh.geometry, protoMesh.matrixWorld, dbId, vbb, COLOR_FILTER_HIGHLIGHT_ABGR);

      let frontMesh = vbbToMesh(vbb, this.room2dMaterial, modelId);
      frontMesh.name = cacheId;

      this._edgeMeshCache.set(cacheId, { front: frontMesh });
      //this.levelSceneWireframe.scene.add(frontMesh);
      this.roomSceneWireframe.add(frontMesh);
    }

    this.viewer.impl.addMainPassOverlay(this.roomSceneWireframe);

  }

  addFloorBoundaryViz(node, forceCompute) {

    if (!this.floorBoundaryVizEnabled) {
      return;
    }

    const protoMesh = this.getFloorMesh(node, !forceCompute);

    if (!protoMesh) {
      this.#queueFloorMeshComputation({ node, whichEffect: 2 });
      return;
    }

    if (!protoMesh?.geometry) {
      return;
    }

    // clone cached floor mesh since it is also used by the mouse hover.
    const cloneMesh = new THREE.Mesh(protoMesh.geometry, this.depthWriteMaterial);
    cloneMesh.matrix.copy(protoMesh.matrix);
    cloneMesh.matrixWorld.copy(protoMesh.matrixWorld);
    cloneMesh.matrixAutoUpdate = false;
    cloneMesh.matrixWorldNeedsUpdate = true;
    cloneMesh.name = node.id;
    this.levelSceneFace.add(cloneMesh);
  }

  #addRoomsToFloorplan(node, vbb, floorBaseElev) {

    const dpr = this.viewer.impl.glrenderer().getPixelRatio();

    const DefaultRoomColor = getContrastColor(this.viewer, 0xff7f7f7f);

    //Color by name code is disabled for now, but we may make it optional in the future.
    const getRoomColor = (roomInfo) => {
      //Note that we would need to linearize the color here if we want to neutralize
      //the gamma correction applied during the blend stage. However, it's less jarring to
      //have the colors look more pastel. See also color determination for the spaces labels.
      let hex = this.facility.facetsManager.colorMaps[FacetTypes.spaces][FacetSpaces.makeId(roomInfo)];
      if (hex) {
        let c = parseInt(hex.slice(1), 16);
        //convert rgb to bgr
        let r = c >> 16 & 0xff;
        let g = c >> 8 & 0xff;
        let b = c & 0xff;
        c = 0xff000000 | b << 16 | g << 8 | r;
        return getContrastColor(this.viewer, c);
      } else {
        return DefaultRoomColor;
      }
    };

    let roomTop = -Infinity;

    let box2 = new THREE.Box3();
    const plane = new THREE.Plane().setComponents(0, 0, 1, -(floorBaseElev + 3));

    let levelNames = [node.id];

    this.facility.forEachRoom((model, roomInfo, catId, roomLevelDbId, fragIds) => {

      let cset = this._roomBoundaryCache.get(roomInfo.externalId);

      if (cset === undefined) {
        //TODO: It's best to use the "computationHeight" from AEC model data for the cutplane here,
        //rather than leaving it to the default (one foot)
        cset = cutMesh(model, fragIds, plane);
        this._roomBoundaryCache.set(roomInfo.externalId, cset || null);

        if (cset) {
          let box = new LmvBox3();
          const fl = model.getFragmentList();
          for (let kk = 0; kk < fragIds.length; kk++) {
            fl.getWorldBounds(fragIds[kk], box2);
            box.union(box2);
          }

          cset.boundingBox = box;
        }
      }

      if (!cset) {
        return;
      }

      roomTop = Math.max(roomTop, cset.boundingBox.max.z);

      const c = DefaultRoomColor; //getRoomColor(roomInfo);

      for (let contour of cset.contours) {
        for (let i = 0; i < contour.length - 1; i++) {

          let p0 = cset.pts[contour[i]];
          let p1 = cset.pts[contour[i + 1]];

          vbb.addSegment(p0.x, p0.y, p1.x, p1.y, 0.0, -1 * dpr, c, roomInfo.dbId, 0, 0, 0);
        }
      }
    }, levelNames, false);

    return roomTop;
  }

  #addWallsToFloorplan(node, vbb, floorBaseElev) {

    const cg = Autodesk.Viewing.Extensions.CompGeom;

    const c = getContrastColor(this.viewer, 0xff7f7f7f);

    const mainModel = this.facility.getPrimaryModel();
    const CutComputationHeight = 1.0 / mainModel.getUnitScale(); //1 meter
    const plane = new THREE.Plane().setComponents(0, 0, 1, -(floorBaseElev + CutComputationHeight));
    const box = new THREE.Box3();
    const box2 = new THREE.Box3();

    for (let [model, ids] of node.ids) {

      const it = model.getInstanceTree();
      const fl = model.getFragmentList();

      const dbId2catId = model.getData().dbId2catId;

      for (let dbIdS of ids) {

        const dbId = parseInt(dbIdS);

        //Currently, in the interest of performance, we only include
        //simple walls in the contextual floor plan. Other categories can be
        //included if desired, but with careful consideration of performance.
        if (dbId2catId[dbId] !== DtConstants.RC.Walls) {
          continue;
        }

        const fragIds = [];
        box.makeEmpty();
        it.enumNodeFragments(dbId, (fragId) => {
          fragIds.push(fragId);
          fl.getWorldBounds(fragId, box2);
          box.union(box2);
        });

        let planeElev = floorBaseElev + CutComputationHeight;
        if (box.min.z > planeElev) {
          planeElev = box.min.z + CutComputationHeight;
        }
        plane.constant = -planeElev;

        let cset = cutMesh(model, fragIds, plane, 1.0 / 1024.0);

        if (!cset) {
          continue;
        }

        cset.triangulate();

        if (cset.triangulationFailed) {








          /*
          let cset2 = cg.MeshFootprint.fixTriangulation(cset);
          if (cset2) {
          	cset = cset2;
          } else {
          	console.log("triangulation failed");
          }
           */}if (cset.indices?.length) {let pts = cset.pts;let vbase = vbb.vcount;for (let j = 0; j < pts.length; j++) {vbb.addVertexPolytriangle(pts[j].x, pts[j].y, c, dbId, 0, 0, 0);
          }

          vbb.addIndices(cset.indices, vbase);
        }
      }
    }

  }

  addFloorplan(nodes) {

    if (!this.floorplanEnabled) {
      return;
    }

    for (const node of nodes) {

      //console.log("floor plan for", node);

      //Get the elevation for the current level
      let elev, baseElev;
      const floorMesh = this.getFloorMesh(node, false);
      if (!floorMesh || this._floorHighlightCache[node.id].isJustBox) {
        return;
      }

      elev = floorMesh.matrixWorld.elements[14]; //The floor mesh world matrix has the floor top as the Z translation component
      baseElev = elev + floorMesh.geometry.boundingBox.min.z; //boundingBox.min.z is negative, so this is really a subtraction to get from top to base elevation

      if (elev === undefined) {
        //console.log("undefined elev");
        //TODO: Get from the model placement
        elev = 0;
        baseElev = 0;
        //elev = level.elev + level.height + model.getData().placementWithOffset.elements[14];
      } else {

        //console.log("elev from cache");
      }
      //If we want the floor plan at the base elevation, but
      //currently we use the top of room geometries as the floorplan elevation
      //so it aligns better with room labels and geometry
      elev = baseElev;

      let vbb = new VertexBufferBuilder();

      //Walls are behind a feature flag
      if (this.facility.loadContext.floorplanlite) {
        //Wall outlines for filtered floors
        this.#addWallsToFloorplan(node, vbb, baseElev);
      }

      const roomTop = this.#addRoomsToFloorplan(node, vbb, baseElev);

      const mesh = vbbToMesh(vbb, this.room2dMaterial, 0 /*modelId*/);
      mesh.matrixWorld.elements[14] = baseElev /*roomTop*/;
      mesh.matrix.copy(mesh.matrixWorld);
      mesh.matrixAutoUpdate = false;
      mesh.matrixWorldNeedsUpdate = false;
      mesh.name = NAME_PREFIX_FLOORPLAN + node.id;

      this.roomSceneWireframe.add(mesh);
    }

    this.viewer.impl.addMainPassOverlay(this.roomSceneWireframe);

  }

  /**
   * @param {DtModel} model
   * @param {Number} dbId
   * @param {string} overlayId
   */
  addElementHighlight(model, dbId, overlayId) {
    const meshes = model.getProxyMeshes(dbId);
    // highlight all host elements' fragments
    this.viewer.impl.addMultipleOverlays(overlayId || ELEMENT_CLONE_OVERLAY, meshes);
  }

  removeElementHighlight(model, dbId, overlayId) {
    let overlay = this.viewer.impl.overlayScenes[overlayId || ELEMENT_CLONE_OVERLAY];


    function removeFromScene(scene, model, dbId) {
      let isDirty = false;
      for (let i = scene.children.length - 1; i >= 0; --i) {
        let obj = scene.children[i];
        if (obj.model === model && obj.dbId === dbId) {
          scene.remove(obj);
          isDirty = true;
        }
      }
      return isDirty;
    }

    const isDirty = removeFromScene(overlay.scene, model, dbId);
    if (isDirty) {
      this.viewer.impl.invalidate(false, false, true);
    }
  }

  /**
   *  Clear the hovering overlay
   *  @deprecated Use clearFacetHighlight instead
   */
  clearHoveringOverlay() {
    // Stop overlayFades that already started
    if (_hoverFadeId) {
      _window.cancelAnimationFrame(_hoverFadeId);
      _hoverFadeId = null;
    }

    // Cancel overlayFades that haven't started yet
    if (_hoverStartTimer) {
      clearTimeout(_hoverStartTimer);
    }

    this.viewer.impl.clearOverlay(HOVER_ROOMS_OVERLAY);
    this.viewer.impl.clearOverlay(HOVER_FLOORS_OVERLAY);
  }

  //TODO: The stream manager (and systems visualizer) should clear only host elements that it explicitly highlighted
  //instead of the whole overlay -- however at this point these are multually exclusive so we can get away with blanket clear.
  clearStreamsOverlay() {
    this.viewer.impl.clearOverlay(ELEMENT_CLONE_OVERLAY);
  }
  clearElementsOverlay(overlayId) {
    this.viewer.impl.clearOverlay(overlayId || ELEMENT_CLONE_OVERLAY);
  }

  /**
   * Clear the levels, rooms, and floorplan overlays which are used to highlight filtered locations
   */
  clearSpaceOverlays() {

    this.removeSpacesFromOverlay((mesh) => {
      //Floorplan geometry is not currently cached for reuse, so we have
      //to free the GPU memory when we clear it
      if (mesh.name.startsWith(NAME_PREFIX_FLOORPLAN)) {
        mesh.geometry.dispose();
        return true;
      }

      return mesh.name.startsWith(NAME_PREFIX_SPACE) || mesh.name.startsWith(NAME_PREFIX_LEVEL);
    });

    this._floorMeshQueue.clear();
    this.levelSceneFace.children.length = 0;
    this.levelSceneWireframe.scene.children.length = 0;
  }

  removeSpacesFromOverlay(matchCB) {
    this.roomSceneWireframe.children = this.roomSceneWireframe.children.filter((mesh) => !matchCB(mesh));
    this.roomSceneBackface.children = this.roomSceneBackface.children.filter((mesh) => !matchCB(mesh));

    if (this.roomSceneWireframe.children.length === 0) {
      this.viewer.impl.removeMainPassOverlay(this.roomSceneWireframe);
    }

    if (this.roomSceneBackface.children.length === 0) {
      this.viewer.impl.removeMainPassOverlay(this.roomSceneBackface);
    }
  }

  /**
   * Remove the selection changed listener
   */
  removeSelectionEventListener() {
    this.viewer.removeEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, this.boundSelectionChange);
  }


  setupObjectFlagHighlight(cb) {

    let models = this.facility.getModels();

    for (let model of models) {
      if (cb) {
        model.getFragmentList()?.setObjectFlagsCB((dbId) => {
          return cb(model, dbId);
        });
      } else {
        model.getFragmentList()?.setObjectFlagsCB(null);
      }
    }
  }

  highlightByObjectFlag(flag, color, comparisonOp = 0) {

    this.viewer.impl.renderer().setGlowFlagAndColor(flag, color, comparisonOp);
    this.viewer.impl.invalidate(false, false, true);

  }

  setupHighlightByCategory() {
    this.glowHighlight = FacetTypes.categories;
    this.setupObjectFlagHighlight((model, dbId) => {
      let cat = model.getData()?.dbId2catId?.[dbId];
      if (!cat) {
        return 0;
      }
      return cat;
    });
  }

  highlightCategory(cat) {
    this.highlightByObjectFlag(!cat ? 0 : cat, GLOW_COLOR, 0);
  }

  setupHighlightByTandemCategory() {
    this.glowHighlight = FacetTypes.tandemCategories;
    this.setupObjectFlagHighlight((model, dbId) => {
      return model.getData()?.dbId2TandemCategory?.[dbId];
    });
  }

  highlightTandemCategory(code) {
    this.highlightByObjectFlag(TandemCategories.code2glowFlag(code), GLOW_COLOR, 2);
  }


  setupHighlightBySystemClass() {
    this.glowHighlight = FacetTypes.systemClasses;
    this.setupObjectFlagHighlight((model, dbId) => {
      return model.getData()?.dbId2SystemClass?.[dbId];
    });
  }

  setupHighlightBySystem() {
    this.glowHighlight = FacetTypes.mepSystems;
    this.setupObjectFlagHighlight((model, dbId) => {
      return model.getData()?.dbId2SystemClass?.[dbId];
    });
  }

  highlightSystemClass(sc) {
    if (sc >= 0) {
      this.highlightByObjectFlag(1 << sc, GLOW_COLOR, 1);
    } else {
      this.highlightByObjectFlag(0, GLOW_COLOR, 1);
    }
  }

  highlightSystem(sc) {
    this.highlightByObjectFlag(sc >= 0 ? sc : 0, GLOW_COLOR, 1);
  }

  setFloorplanEnabled(value) {
    this.floorplanEnabled = value;
  }

  setFloorBoundaryVizEnabled(value) {
    this.floorBoundaryVizEnabled = value;
  }

  update(timestamp) {

    //Incremental processing of floor footprints

    if (this._floorMeshQueue.size === 0) {
      return;
    }

    const [k, item] = this._floorMeshQueue.entries().next().value;
    if (item.whichEffect === 1) {
      this.addFloorEdgeHighlight(item.node, true);
      this.viewer.impl.invalidate(true);
    } else {
      this.addFloorBoundaryViz(item.node, true);
      this.viewer.impl.invalidate(false, false, true);
    }

    this._floorMeshQueue.delete(k);
  }

  _dbg2d() {
    let vbb = new VertexBufferBuilder();
    vbb.addSegment3D(50, 100, 0, 1050, 100, 0, -1, -6, 0xff000000, 0, 0);
    vbb.addSegment3D(1050, 100, 0, 1050, 1100, 0, -1001, -6, 0xff000000, 0, 0);

    vbb.addSegment3D(50, 100, 0, 1050, 100, 0, -1, -6, 0xff7fff00, 0, 10);
    vbb.addSegment3D(1050, 100, 0, 1050, 1100, 0, -1001, -6, 0xff7fff00, 0, 10);

    let mesh = vbbToMesh(vbb, this.room2dMaterial, 0);
    this.roomSceneWireframe.add(mesh);
  }
}